import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import cn from 'classnames';

import { Icon } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import {
  ActionMenu,
  ActionOption,
} from 'components/ActionMenu/ActionMenu.component';
import { SocietyDashboardAttributes } from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './SocietyDashboardInfo.module.scss';

interface SocietyDashboardInfoProps {
  dashboardInfo: SocietyDashboardAttributes;
}

enum dropdownLabel {
  Today = 'Today',
  Last_7_days = 'Last 7 days',
  Last_30_days = 'Last 30 days',
  Total = 'Total',
}

export const SocietyDashboardInfo: React.FC<SocietyDashboardInfoProps> = ({
  dashboardInfo,
}) => {
  const {
    subscribers_count,
    this_month_subscribers_count,
    live_touchpoint_applications_count,
    live_touchpoints_count,
    live_touchpoints_details: liveEvents,
  } = dashboardInfo;
  const router = useRouter();

  const [viewCount, setViewCount] = useState<number>(0);
  const [activeViewCountLabel, setActiveViewCountLabel] =
    useState<dropdownLabel>(dropdownLabel.Last_30_days);
  const [linkCount, setLinkCount] = useState<number>(0);
  const [activeClicksCountLabel, setActiveClicksCountLabel] =
    useState<dropdownLabel>(dropdownLabel.Last_30_days);

  useEffect(() => {
    setViewCount(dashboardInfo.views_in_last_30);
    setLinkCount(dashboardInfo.clicks_in_last_30);
  }, []);

  const viewsDropdownMenu: ActionOption[] = [
    {
      id: '1',
      label: dropdownLabel.Today,
      onClick: () => {
        setActiveViewCountLabel(dropdownLabel.Today);
        setViewCount(dashboardInfo.views_today);
      },
    },
    {
      id: '2',
      label: dropdownLabel.Last_7_days,
      onClick: () => {
        setActiveViewCountLabel(dropdownLabel.Last_7_days);
        setViewCount(dashboardInfo.views_in_last_7);
      },
    },
    {
      id: '3',
      label: dropdownLabel.Last_30_days,
      onClick: () => {
        setActiveViewCountLabel(dropdownLabel.Last_30_days);
        setViewCount(dashboardInfo.views_in_last_30);
      },
    },
    {
      id: '4',
      label: dropdownLabel.Total,
      onClick: () => {
        setActiveViewCountLabel(dropdownLabel.Total);
        setViewCount(dashboardInfo.views_total);
      },
    },
  ];

  const clicksDropdownMenu: ActionOption[] = [
    {
      id: '1',
      label: dropdownLabel.Today,
      onClick: () => {
        setActiveClicksCountLabel(dropdownLabel.Today);
        setLinkCount(dashboardInfo.clicks_today);
      },
    },
    {
      id: '2',
      label: dropdownLabel.Last_7_days,
      onClick: () => {
        setActiveClicksCountLabel(dropdownLabel.Last_7_days);
        setLinkCount(dashboardInfo.clicks_in_last_7);
      },
    },
    {
      id: '3',
      label: dropdownLabel.Last_30_days,
      onClick: () => {
        setActiveClicksCountLabel(dropdownLabel.Last_30_days);
        setLinkCount(dashboardInfo.clicks_in_last_30);
      },
    },
    {
      id: '4',
      label: dropdownLabel.Total,
      onClick: () => {
        setActiveClicksCountLabel(dropdownLabel.Total);
        setLinkCount(dashboardInfo.clicks_total);
      },
    },
  ];

  const createNewEvent = () => {
    router.push(`${PAGE_ROUTES.EVENTS_CREATE}?step=1&type=Event`);
  };

  const redirectToCommunity = () => {
    router.push(PAGE_ROUTES.COMMUNITY);
  };

  const getLiveEvents = useMemo(() => {
    const liveCampaigns =
      liveEvents &&
      liveEvents.map((campaign) => {
        return {
          id: campaign.id,
          label: `${campaign.title} (${campaign.application_count})`,
          onClick: () => router.push(`${PAGE_ROUTES.CAMPAIGNS}/${campaign.id}`),
        };
      });

    return liveCampaigns as ActionOption[];
  }, [liveEvents]);

  const renderViewEventsCta = () =>
    liveEvents.length > 1 ? (
      <ActionMenu
        options={getLiveEvents}
        className={styles.liveEventsAction}
        actionElement={
          <ButtonV3
            isLoading={!getLiveEvents.length}
            className={styles.infoAction}
            color="secondary"
            endIcon="icon_arrow-down"
          >
            View
          </ButtonV3>
        }
      />
    ) : (
      <ButtonV3
        className={styles.infoAction}
        color="secondary"
        onClick={() =>
          router.push(`${PAGE_ROUTES.CAMPAIGNS}/${liveEvents[0].id}`)
        }
      >
        View
      </ButtonV3>
    );

  return (
    <div className={styles.SocietyDashboardInfo}>
      <div
        className={cn(styles.SocietyDashboardInfoCard, {
          [styles.active]: true,
        })}
      >
        <div className={styles.infoSection}>
          <p className={styles.title}>Members</p>
          <p className={styles.count}>{subscribers_count}</p>
          <div className={styles.latestData}>
            <p className={styles.latestCount}>
              +{this_month_subscribers_count}
            </p>
            <p>this month</p>
          </div>
        </div>
        <ButtonV3
          className={styles.infoAction}
          color="secondary"
          onClick={redirectToCommunity}
        >
          View more
        </ButtonV3>
      </div>

      <div className={styles.SocietyDashboardInfoCard}>
        <div className={styles.infoSection}>
          <p className={styles.title}>Registrations</p>
          <p className={styles.count}>{live_touchpoint_applications_count}</p>
          <p
            className={cn(styles.liveCount, {
              [styles.noLiveEvent]: !live_touchpoints_count,
            })}
          >
            {`${
              live_touchpoints_count > 0 ? live_touchpoints_count : 'No'
            } live events`}
          </p>
        </div>
        {liveEvents && liveEvents.length > 0 ? (
          renderViewEventsCta()
        ) : (
          <ButtonV3
            className={styles.infoAction}
            color="primary"
            onClick={createNewEvent}
          >
            Create new event
          </ButtonV3>
        )}
      </div>

      <div className={styles.SocietyDashboardInfoCard}>
        <div className={styles.interactionData}>
          <div className={styles.dataContainer}>
            <p className={styles.dataTitle}>Profile views</p>
            <ActionMenu
              className={styles.calendarAction}
              listClassName={styles.calendarActionMenu}
              options={viewsDropdownMenu}
              actionElement={
                <div className={styles.selectedDuration}>
                  <p>{activeViewCountLabel}</p>
                  <Icon iconName="icon_arrow-down" size="xxsmall" />
                </div>
              }
            />
          </div>
          <p className={styles.interactViews}>{viewCount}</p>
        </div>
        <div className={styles.divider}></div>
        <div className={styles.interactionData}>
          <div className={styles.dataContainer}>
            <p className={styles.dataTitle}>Link clicks</p>
            <ActionMenu
              className={styles.calendarAction}
              listClassName={styles.calendarActionMenu}
              options={clicksDropdownMenu}
              actionElement={
                <div className={styles.selectedDuration}>
                  <p>{activeClicksCountLabel}</p>
                  <Icon iconName="icon_arrow-down" size="xxsmall" />
                </div>
              }
            />
          </div>
          <p className={styles.interactViews}>{linkCount}</p>
        </div>
      </div>
    </div>
  );
};
